import { FC, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import type {
  SectionView,
  TemplateSectionsPhase,
} from "~/features/cases/hooks/useFtueTemplateSectionsView";
import { usePhaseProgress } from "~/features/cases/hooks/usePhaseProgress";
import { LiveSectionsView } from "./LiveSectionsView";
import { ReadingView } from "./ReadingView";

const PHASES = [
  {
    key: "read",
    labelId: "ftue.templateBuild.phase.read",
    labelDefault: "Reading Your Example Report",
    descId: "ftue.templateBuild.phase.read.desc",
    descDefault: "Analyzing your formatting, headings, and structure.",
  },
  {
    key: "split",
    labelId: "ftue.templateBuild.phase.split",
    labelDefault: "Identifying Reusable Sections",
    descId: "ftue.templateBuild.phase.split.desc",
    descDefault:
      "Breaking your report into sections that can be filled with new content.",
  },
  {
    key: "prompt",
    labelId: "ftue.templateBuild.phase.prompt",
    labelDefault: "Building Your Reusable Template",
    descId: "ftue.templateBuild.phase.prompt.desc",
    descDefault:
      "Creating a blueprint so every future report matches this format.",
  },
  {
    key: "draft",
    labelId: "ftue.templateBuild.phase.draft",
    labelDefault: "Generating a Sample Report",
    descId: "ftue.templateBuild.phase.draft.desc",
    descDefault: "Writing a preview to show what your reports will look like.",
  },
] as const;

/** Phase → step index for the 4-step vertical list on the left. */
const PHASE_TO_STEP_IDX: Record<TemplateSectionsPhase, number> = {
  reading: 0,
  splitting: 1,
  sections_ready: 2,
  generating: 3,
  ready: 3,
  failed: 3,
};

/**
 * Coarse mapping from a high-level phase + per-section progress to a 0..1
 * "norm" value passed to {@link usePhaseProgress}'s `dataAnchor`. The numbers
 * are coordinated with the PHASES weights so that each phase visually occupies
 * roughly its weighted segment.
 */
function deriveDataAnchor(
  phase: TemplateSectionsPhase,
  totalSections: number,
  completedCount: number
): number {
  switch (phase) {
    case "reading":
      return 0;
    case "splitting":
      return 0.25;
    case "sections_ready":
      return 0.55;
    case "generating": {
      if (totalSections === 0) return 0.55;
      const fraction = Math.max(0, Math.min(1, completedCount / totalSections));
      return 0.55 + 0.4 * fraction;
    }
    case "ready":
      return 1;
    case "failed":
      return 1;
  }
}

function getEtaLabel(remaining: number): {
  id: string;
  defaultMessage: string;
} {
  if (remaining > 50)
    return {
      id: "ftue.templateBuild.eta.aboutAMinute",
      defaultMessage: "About a minute left",
    };
  if (remaining > 35)
    return {
      id: "ftue.templateBuild.eta.lessMinute",
      defaultMessage: "Less than a minute left",
    };
  if (remaining > 20)
    return {
      id: "ftue.templateBuild.eta.about30s",
      defaultMessage: "About 30 seconds left",
    };
  if (remaining > 8)
    return {
      id: "ftue.templateBuild.eta.fewSeconds",
      defaultMessage: "A few seconds left",
    };
  return {
    id: "ftue.templateBuild.eta.almostReady",
    defaultMessage: "Almost ready",
  };
}

type StepState = "done" | "active" | "pending";

function getStepState(stepIndex: number, activeStepIdx: number): StepState {
  if (stepIndex < activeStepIdx) return "done";
  if (stepIndex === activeStepIdx) return "active";
  return "pending";
}

interface FtueTemplateBuildProgressProps {
  onSkip: () => void;
  documentUrl?: string;
  sections: SectionView[];
  phase: TemplateSectionsPhase;
  totalSections: number;
  completedCount: number;
}

export const FtueTemplateBuildProgress: FC<FtueTemplateBuildProgressProps> = ({
  onSkip,
  documentUrl,
  sections,
  phase,
  totalSections,
  completedCount,
}) => {
  const dataAnchor = useMemo(
    () => deriveDataAnchor(phase, totalSections, completedCount),
    [phase, totalSections, completedCount]
  );

  const { phaseIdx, intra, remaining } = usePhaseProgress(undefined, {
    dataAnchor,
  });
  const eta = getEtaLabel(remaining);

  const isReading = phase === "reading";
  const activeStepIdx = PHASE_TO_STEP_IDX[phase];

  return (
    <div className="flex flex-col lg:flex-row min-h-ftue px-4 gap-6 max-w-7xl mx-auto w-full">
      {/* Left panel: status */}
      <div className="lg:w-1/3 flex flex-col justify-center gap-6 py-8">
        <div>
          <h1
            className="text-2xl font-semibold text-foreground leading-tight mb-2"
            aria-live="polite"
          >
            <FormattedMessage
              id="ftue.templateBuild.title"
              defaultMessage="Turning Your Report Into a Reusable Template"
            />
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-[360px]">
            <FormattedMessage
              id="ftue.templateBuild.subtitle"
              defaultMessage="Your report format is being learned — future reports will match it exactly."
            />
          </p>
        </div>

        {/* Segmented progress bar */}
        <div className="flex flex-col gap-2.5 w-full">
          <div className="flex gap-1 w-full max-w-[320px]">
            {PHASES.map((_, i) => {
              let fill = 0;
              if (i < phaseIdx) fill = 1;
              else if (i === phaseIdx) fill = intra;
              return (
                <div
                  key={i}
                  className="flex-1 h-[5px] rounded-full overflow-hidden"
                  style={{ background: "hsl(var(--border))" }}
                >
                  <div
                    className="h-full rounded-full bg-blue-600"
                    style={{
                      width: `${fill * 100}%`,
                      transition: "width 160ms linear",
                    }}
                  />
                </div>
              );
            })}
          </div>
          <span className="text-xs text-muted-foreground">
            <FormattedMessage id={eta.id} defaultMessage={eta.defaultMessage} />
          </span>
        </div>

        {/* Vertical step list */}
        <div className="flex flex-col gap-3.5 mt-1">
          {PHASES.map((phaseDescriptor, i) => {
            const state = getStepState(i, activeStepIdx);
            return (
              <div key={phaseDescriptor.key} className="flex gap-3 relative">
                {/* Connecting line */}
                {i < PHASES.length - 1 && (
                  <div
                    className="absolute w-px"
                    style={{
                      left: 7,
                      top: 18,
                      bottom: -18,
                      background:
                        i < activeStepIdx ? "#2563eb" : "hsl(var(--border))",
                      transition: "background 320ms ease",
                    }}
                  />
                )}
                {/* Dot / check / ring */}
                <div
                  className="relative z-10 shrink-0 flex items-center justify-center mt-[3px]"
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 999,
                    background: state === "pending" ? "transparent" : "#2563eb",
                    border:
                      state === "pending"
                        ? "1.5px solid hsl(var(--border))"
                        : "none",
                    transition:
                      "background 320ms ease, border-color 320ms ease",
                  }}
                >
                  {state === "done" && (
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2.5 6.5L5 9L9.5 3.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {state === "active" && (
                    <div
                      className="cascade-animate absolute inset-[-4px] rounded-full"
                      style={{
                        border: "1.5px solid rgba(37,99,235,0.4)",
                        animation: "cascade-pulse-dot 1.6s ease-out infinite",
                      }}
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <span
                    className="text-sm"
                    style={{
                      color:
                        state === "active"
                          ? "hsl(var(--foreground))"
                          : "hsl(var(--muted-foreground))",
                      opacity: state === "pending" ? 0.7 : 1,
                      fontWeight: state === "active" ? 500 : 400,
                      transition: "color 320ms ease",
                    }}
                  >
                    <FormattedMessage
                      id={phaseDescriptor.labelId}
                      defaultMessage={phaseDescriptor.labelDefault}
                    />
                  </span>
                  {state === "active" && (
                    <span className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      <FormattedMessage
                        id={phaseDescriptor.descId}
                        defaultMessage={phaseDescriptor.descDefault}
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <Button
            variant="link"
            onClick={onSkip}
            className="text-muted-foreground hover:text-foreground px-0"
          >
            <FormattedMessage
              id="ftue.templateBuild.skip"
              defaultMessage="Skip Preview"
            />
          </Button>
        </div>
      </div>

      {/* Right panel: reading view → live sections */}
      <div className="lg:w-2/3 flex flex-col py-8">
        <div className="flex items-center gap-2 mb-3 text-muted-foreground">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
            <path d="M14 2v4a2 2 0 0 0 2 2h4" />
            <path d="M10 9H8" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
          </svg>
          <span className="text-sm">
            {isReading ? (
              <FormattedMessage
                id="ftue.templateBuild.uploadedReportLabel"
                defaultMessage="Your Uploaded Report"
              />
            ) : (
              <FormattedMessage
                id="ftue.templateBuild.sectionsHeader"
                defaultMessage="Template sections"
              />
            )}
          </span>
        </div>
        <div
          className="flex-1 min-h-[400px] flex flex-col overflow-hidden"
          style={{ maxHeight: "calc(100vh - 300px)" }}
        >
          {isReading ? (
            <ReadingView documentUrl={documentUrl} />
          ) : (
            <LiveSectionsView sections={sections} phase={phase} />
          )}
        </div>
      </div>
    </div>
  );
};
